import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { CreateStockAlertDto, StockAlertDto } from '@services/dtos'
import { stockAlertFormSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Alert, Grid } from '@mui/material'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { FormCrudHeader } from './FormCrudHeader'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { productCatalogService } from '@services/ProductCatalogService'
import { createStockAlert, updateStockAlert } from '@state/reducers/stockAlertReducer'

type Props = {
  editingStockAlert?: StockAlertDto | null
  onClose: () => void
}

const StockAlertForm: React.FC<Props> = ({
  editingStockAlert,
  onClose
}) => {
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateStockAlertDto>({
    defaultValues: editingStockAlert
      ? {
          thresholdUnits: editingStockAlert.thresholdUnits,
          productGenericName: editingStockAlert.productGenericName,
        }
      : {},
    resolver: yupResolver(stockAlertFormSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateStockAlertDto) => {
    setIsSubmitting(true)

    if (editingStockAlert) {
      dispatch(updateStockAlert({ id: editingStockAlert.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('L\'alerte de stock a été modifiée avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le seuil n\'est pas déjà configuré pour cette DCI'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createStockAlert(data))
        .unwrap()
        .then(() => {
          cb('L\'alerte de stock a été créée avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le seuil n\'est pas déjà configuré pour cette DCI'))
        .finally(() => setIsSubmitting(false))
    }
  }

  const fetchAllGenericNames = async (search: string) => {
    const page = await productCatalogService.getAllGenericNames(search)
    return page?.datas ?? []
  }

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingStockAlert}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Alerte stock
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12}>
                <ServerAutocomplete<string>
                  autoFetchOnMount
                  value={editingStockAlert ? editingStockAlert.productGenericName : null}
                  fetchService={fetchAllGenericNames}
                  onChange={(value: string | null) => {
                    setValue('productGenericName', value || '')
                  }}
                  getValue={(value) => value || null}
                  getOptionLabel={(value) => value}
                  debounceMs={300}
                  placeholder="Rechercher une DCI"
                  label="DCI"
                  minSearchLength={0}
                  helperText={errors.productGenericName?.message}
                  error={!!errors.productGenericName}
                />
              </Grid>
                  
              <Grid item xs={12}>
                <InputField
                  control={control}
                  name="thresholdUnits"
                  label="Seuil quantité"
                  type="number"
                />

                <Alert severity="info" className="mt-2">
                  {'Le seuil est par défaut de 0, il n\'est pas nécessaire de le spécifier'}
                </Alert>
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default StockAlertForm
